/**
 * Session reset for login/logout so stale client state does not survive an auth change.
 * Does not (and cannot) wipe the browser HTTP disk cache. Deploy freshness comes from
 * no-cache index.html + hashed static assets + a full page load.
 */

/** Keys required to finish login, 2FA, SSO, or post-login redirect. */
const SESSION_KEYS_TO_PRESERVE = new Set([
  "token",
  "refresh_token",
  "access_token",
  "access_timestamp",
  "token_decoded",
  "2FAuth",
  "username",
  "pendingLogin",
  "pendingSocialAccountLinking",
  "ssoLogin",
  "googleAuthAction",
  "linkedinAuthAction",
  "linkedinCallbackProcessed",
  "googleCallbackProcessed",
  "prevPath",
]);

const LOCAL_STORAGE_PREFIXES_TO_CLEAR = [
  "wsg_local_prefs_",
  "practiceIndustryIcon_",
  "event_registration_sections_",
  "wsg:profilePhotoVersion:",
];

const LOCAL_STORAGE_KEYS_TO_CLEAR = [
  "lastRoute",
  "selectedFirm",
  "selectedTab",
  "firmCountWorldwide",
  "firmCountPrimary",
  "totalProfessionals",
  "isStaffMode",
];

export type ClearApplicationCacheOptions = {
  /** Extra sessionStorage keys to keep (in addition to login/redirect keys). */
  preserveSessionKeys?: string[];
  /**
   * Full logout only. Wipes every sessionStorage key.
   * Login and 401 must leave prevPath / pendingLogin / SSO flags in place.
   */
  clearAllSessionStorage?: boolean;
};

async function clearBrowserCacheStorage(): Promise<void> {
  if (typeof window === "undefined" || !("caches" in window)) {
    return;
  }

  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch (error) {
    console.warn("[clearApplicationCache] Failed to clear Cache Storage:", error);
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (error) {
    console.warn("[clearApplicationCache] Failed to unregister service workers:", error);
  }
}

function clearApplicationLocalStorage(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) {
        continue;
      }

      if (LOCAL_STORAGE_KEYS_TO_CLEAR.includes(key)) {
        keysToRemove.push(key);
        continue;
      }

      if (LOCAL_STORAGE_PREFIXES_TO_CLEAR.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("[clearApplicationCache] Failed to clear localStorage:", error);
  }
}

function clearApplicationSessionStorage(options: ClearApplicationCacheOptions): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (options.clearAllSessionStorage) {
      sessionStorage.clear();
      return;
    }

    const preserve = new Set([
      ...SESSION_KEYS_TO_PRESERVE,
      ...(options.preserveSessionKeys ?? []),
    ]);

    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || preserve.has(key)) {
        continue;
      }
      keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn("[clearApplicationCache] Failed to clear sessionStorage:", error);
  }
}

export async function clearApplicationCache(
  options: ClearApplicationCacheOptions = {},
): Promise<void> {
  await Promise.all([clearBrowserCacheStorage(), unregisterServiceWorkers()]);
  clearApplicationLocalStorage();
  clearApplicationSessionStorage(options);
}

/** Full page navigation after login so the browser loads a fresh document. */
export function redirectAfterLogin(path: string): void {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  window.location.replace(normalized);
}
